import { Form, Input, Select, Skeleton, Space, Typography } from 'antd';

import {
  ConfirmDeletingElement,
  EditPageButtons,
  Error403Unauthorized,
} from '@app/components';
import { OFF, ONE_TIME_CODE, RULES_IS_REQUIRE } from '@app/constants';
import { ERROR } from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';

import { useAddPosition } from '@pages/Employees/AddPosition/hooks/useAddPosition';
import { usePositions } from '@pages/Employees/Positions/hooks/usePositions';

import type { FC } from 'react';
import '@pages/Employees/AddPosition/styles/AddPosition.desktop.scss';
import '@pages/Employees/AddPosition/styles/AddPosition.tablet.scss';

export const AddPosition: FC = () => {
  const {
    isLoading,
    form,
    submitForm,
    serverError,
    setServerError,
    editablePositionId,
    modalOpen,
    setModalOpen,
    handleCancel,
    deletePosition,
    hasUserRightToEditPosition,
    setInputData,
    initialValues,
    roleOptions,
  } = useAddPosition();

  const { hasUserRightToCreatePosition, hasUserRightToViewPositions } =
    usePositions();

  if (hasUserRightToViewPositions) {
    if (
      (!hasUserRightToEditPosition && editablePositionId) ||
      (!hasUserRightToCreatePosition && !editablePositionId)
    )
      return <Error403Unauthorized />;
  }

  if (isLoading && editablePositionId) {
    return <Skeleton active />;
  }

  return (
    <div className={`${PREFIX}add-position`}>
      <Form
        form={form}
        onFinish={submitForm}
        className="form"
        autoComplete={OFF}
        initialValues={initialValues}
      >
        <Space direction="vertical" size="large" className="form-space">
          <div className="container">
            <Typography.Text
              strong
              className="label-require"
              data-testid="position-name-title"
            >
              Название
            </Typography.Text>

            <Form.Item name="Name" className="mg-0" rules={RULES_IS_REQUIRE}>
              <Input
                data-testid="position-name"
                autoComplete={ONE_TIME_CODE}
                maxLength={60}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Name',
                    isEditPage: Boolean(editablePositionId),
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              className="label"
              strong
              data-testid="position-description-title"
            >
              Описание
            </Typography.Text>

            <Form.Item name="Description" className="mg-0">
              <Input.TextArea
                data-testid="position-description"
                autoComplete={ONE_TIME_CODE}
                rows={4}
                maxLength={260}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Description',
                    isEditPage: Boolean(editablePositionId),
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              className="label"
              strong
              data-testid="position-roles-title"
            >
              Роли
            </Typography.Text>

            <Form.Item name="RoleKeys" className="mg-0">
              <Select
                mode="multiple"
                allowClear
                placeholder="Выберите роли"
                options={roleOptions}
                data-testid="position-roles"
              />
            </Form.Item>
          </div>

          <EditPageButtons
            serverError={serverError}
            errorType={ERROR}
            setServerError={setServerError}
            isLoading={isLoading}
            setModalOpen={setModalOpen}
            handleCancel={handleCancel}
            isEditPage={Boolean(editablePositionId)}
          />
        </Space>
      </Form>

      <ConfirmDeletingElement
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        handleDelete={deletePosition}
        content="Вы уверены, что хотите удалить должность?"
        title="Удаление должности"
        modalDataTestId="delete-position-popup"
        titleDataTestId="delete-position-title"
        descriptionDataTestId="delete-position-description"
      />
    </div>
  );
};
